import { useState } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

/**
 * ShareEventButton — blue pill that opens a dialog with a copyable event link.
 * Used on the EventDetail screen so runners can share the event with friends.
 *
 * Uses the native Web Share API when available, else falls back to a copy
 * dialog inside the app.
 */
export function ShareEventButton({ event, className = '' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = typeof window !== 'undefined'
    ? `${window.location.origin}/events/${event.id}`
    : `/events/${event.id}`;

  async function handleShare(e) {
    e.preventDefault();
    e.stopPropagation();
    // Prefer the native share sheet on supported devices for one-tap UX.
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.name,
          text: `Join me at ${event.name} on RIVL`,
          url,
        });
        return;
      } catch {
        // User cancelled or share failed — fall back to the dialog.
      }
    }
    setOpen(true);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  }

  return (
    <>
      <Button
        type="button"
        onClick={handleShare}
        data-testid="event-share-btn"
        className={`bg-brand-blue hover:bg-brand-blue/90 text-white border-brand-blue ${className}`}
      >
        <Share2 className="h-4 w-4 mr-2" /> Share
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="share-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">
              Share this event
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Send this link to anyone — they can open the event directly in RIVL.
            </DialogDescription>
          </DialogHeader>

          <div className="rivl-card p-3 flex items-center gap-2 border-brand-blue/40">
            <div className="flex-1 min-w-0 text-xs font-mono truncate" data-testid="share-event-url">
              {url}
            </div>
            <Button
              size="sm"
              type="button"
              onClick={copy}
              data-testid="share-event-copy"
              className="h-8 shrink-0 bg-brand-blue hover:bg-brand-blue/90 text-white"
            >
              {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
            </Button>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            className="w-full mt-2"
          >
            <X className="h-4 w-4 mr-2" /> Close
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
